// QuotationReport.tsx
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Navigate } from "react-router-dom";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import Export from "../../../../formHelper/export";
import Search from "../../../../formHelper/search";
import Pagination from "../../../../formHelper/pagination";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { defaulSetDateFilter } from "../../../../formHelper/dateFilter";
import exportApiCall from "../../../../apiHelper/export";
import FilterDropdown from "../../../../formHelper/filterDropDown";
import Loader from "../../../../formHelper/Loader";
import Table from "../../../../formHelper/list";
import dayjs from "dayjs";
import type { RootState, AppDispatch } from "../../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";

interface Branch {
  id: number;
  name: string;
}

interface CustomerDD {
  id: number;
  company_name: string;
  owner_name: string;
  address: string;
  state_id: number;
  other_state?: any;
  city?: any;
  mobile_no?: any;
  email_id?: any;
  landline_no?: any;
  pin_code?: any;
  customer_name?: any;
  owner?: {
    id: number;
    name: string;
  } | null;
}

interface ProductRow {
  [key: string]: any;
}

interface PendingQuotationFormData {
  current_follow_date: string;
  reason: string;
  reason_status_id: string | number | null;
}

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

interface Quotation {
  [key: string]: any;
  id: number;
  quotation_details?: ProductRow[];
  company_details?: { company_name?: string };
  branch_details?: { name?: string };
  pending_quotation_details?: {
    reason?: string;
    reason_status_id?: number | string;
    status_code?: string;
  };
  unique_quotation_no?: string;
  total_amount?: number;
  lead_from?: string;
  created_at?: string;
}

/* ---------- Debounce hook ---------- */
const useDebounced = <T,>(value: T, ms = 400) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const QuotationReport: React.FC = () => {
  const backendRoute = all_routes.backend;

  const [list, setList] = useState<Quotation[]>([]);
  const [formDataPendingQuotation, setFormDataPendingQuotation] =
    useState<PendingQuotationFormData>({
      current_follow_date: "",
      reason: "",
      reason_status_id: null,
    });

  const [query, setQuery] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      return 5;
    }
    return 10;
  });
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<number[]>([]);
  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [principalFilter, setPrincipalFilter] = useState<number[]>([]);
  const [currencyFilter, setCurrencyFilter] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);

  const [companyList, setCompanyList] = useState<CustomerDD[]>([]);
  const [ownerList, setOwnerList] = useState<Branch[]>([]);
  const [quotationTypeList, setQuotationTypeList] = useState<Branch[]>([]);
  const [notificationList, setNotificationList] = useState<Branch[]>([]);
  const [currencyList, setCurrencyList] = useState<Branch[]>([]);
  const [branchList, setBranchList] = useState<Branch[]>([]);
  const [stateList, setStateList] = useState<Branch[]>([]);
  const [paymentAdvanceDate, setPaymentAdvanceDate] = useState<Branch[]>([]);
  const [searchText, setSearchText] = useState("");

  const [productRows, setProductRows] = useState<ProductRow[]>([]);
  const [quotationReason, setQuotationReason] = useState<Quotation | null>(
    null
  );
  const [descReason, setDescReason] = useState<string | null>(null);
  const [statusList, setStatusList] = useState<Branch[]>([]);

  /* export state */
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  /* ---------- fetchList ---------- */
  const fetchList = useCallback(
    async ({ page = 1, perPage = 10, search = "" }) => {
      setLoading(true);
      if (search) {
        setLoadingSearch(true);
      }
      try {
        const result = await apiRequestCall("post", backendRoute.getQuotation, {
          data: {
            page,
            per_page: perPage,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            search: search.trim(),
            owner_list: ownerFilter,
            branch_list: branchFilter,
            principal_list: principalFilter,
            currency_list: currencyFilter,
            status_list: statusFilter,
          },
        });

        const res = result?.data?.data;
        if (res) {
          setList(res.data || []);
          setCurrentPage(res.current_page);
          setPerPage(res.per_page);
          setTotalItems(res.total);
        } else {
          setList([]);
        }
      } catch (error) {
        console.error("Failed to fetch quotations:", error);
        setList([]);
      } finally {
        setLoading(false);
        setLoadingSearch(false);
      }
    },
    [
      backendRoute.getQuotation,
      endDate,
      startDate,
      ownerFilter,
      branchFilter,
      principalFilter,
      currencyFilter,
      statusFilter,
    ]
  );

  /* ---------- Export helpers (polling) ---------- */
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  // Poll API for local download
  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  const exportFile = useCallback(async () => {
    try {
      setExporting(true);
      setDownloading(false);

      // POST same filters, request download
      const result = await apiRequestCall("post", backendRoute.getQuotation, {
        data: {
          search: searchText,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          owner_list: ownerFilter,
          branch_list: branchFilter,
          principal_list: principalFilter,
          currency_list: currencyFilter,
          status_list: statusFilter,
          download: true,
        },
      });

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;
      console.log("result");
      console.log(result);

      if (!filename) {
        setExporting(false);
        alert("Export request failed. Please try again.");
        return;
      }

      // Otherwise poll for file readiness
      pollForFile(filename);
    } catch (error) {
      console.error("Export request failed:", error);
      setExporting(false);
      setDownloading(false);
      alert("Export failed. Please try again.");
    }
  }, [
    backendRoute.getQuotation,
    startDate,
    endDate,
    ownerFilter,
    branchFilter,
    principalFilter,
    currencyFilter,
    statusFilter,
    searchText,
  ]);

  /* ---------- Dropdown fetch helpers ---------- */
  const fetchDropdownData = useCallback(
    async (
      endpoint: string,
      setter: React.Dispatch<React.SetStateAction<Branch[]>>
    ) => {
      try {
        const result = await apiRequestCall(
          "post",
          backendRoute[endpoint as keyof typeof backendRoute]
        );
        const raw = result?.data?.data;
        if (raw && typeof raw === "object") {
          setter(
            Object.entries(raw).map(([id, name]) => ({
              id: +id,
              name: String(name),
            }))
          );
        } else {
          setter([]);
        }
      } catch (error) {
        console.error(`Failed to fetch ${endpoint}:`, error);
        setter([]);
      }
    },
    [backendRoute]
  );

  const getCompanyList = useCallback(async () => {
    try {
      const result = await apiRequestCall("post", backendRoute.getCompanyDD);
      const raw = result?.data?.data;
      if (Array.isArray(raw)) {
        setCompanyList(
          raw.map((item: any) => ({
            id: item.id,
            company_name: item.company_name || "",
            owner_name: item?.owner?.name || "",
            address: item.address || "",
            state_id: item.state_id,
            other_state: item.other_state,
            city: item.city,
            email_id: item.email_id,
            pin_code: item.pin_code,
            mobile_no: item.mobile_no,
            landline_no: item.landline_no,
            customer_name: item.customer_name,
            owner: item.owner || null,
          }))
        );
      } else {
        setCompanyList([]);
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
      setCompanyList([]);
    }
  }, [backendRoute.getCompanyDD]);

  const handleRow = (row: Quotation) => {
    const bulletReason =
      row?.pending_quotation_details?.reason
        ?.split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => `• ${item}`)
        .join("\n") || "";

    setFormDataPendingQuotation({
      current_follow_date: "",
      reason: "",
      reason_status_id:
        row?.pending_quotation_details?.reason_status_id || null,
    });
    setDescReason(bulletReason);
    setQuotationReason(row);
  };

  const searchTextData = (text: string) => {
    setSearchText(text);
  };
  /* ---------- Effects ---------- */
  useEffect(() => {
    getCompanyList();
    fetchDropdownData("getOwnerDD", setOwnerList);
    fetchDropdownData("getQuotationTypeDD", setQuotationTypeList);
    fetchDropdownData("getNotificationDD", setNotificationList);
    fetchDropdownData("getStateDD", setStateList);
    fetchDropdownData("getBranchDD", setBranchList);
    fetchDropdownData("getPaymentAdvanceDD", setPaymentAdvanceDate);
    fetchDropdownData("getStatusDD", setStatusList);
    fetchDropdownData("getCurrencyDD", setCurrencyList);
  }, []);

  useEffect(() => {
    fetchList({ page: currentPage, perPage, search: searchText });
  }, [
    fetchList,
    currentPage,
    perPage,
    startDate,
    endDate,
    ownerFilter,
    branchFilter,
    principalFilter,
    currencyFilter,
    statusFilter,
    searchText,
  ]);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "Sr",
        render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
      },
      { key: "company_details.company_name", label: "Company Name" },
      { key: "unique_quotation_no", label: "Quotation No" },
      { key: "total_amount", label: "Total" },
      { key: "lead_from", label: "Lead From" },
      { key: "branch_details.name", label: "Branch" },
      {
        key: "created_at",
        label: "Created Date",
        render: (row: any) =>
          row.created_at
            ? dayjs(row.created_at, "DD-MM-YYYY").format("DD/MM/YYYY")
            : "",
      },
      {
        key: "status",
        label: "Status",
        render: (row: Quotation) => (
          <div className="hstack gap-2 fs-15">
            {row?.pending_quotation_details?.status_code === "open" && (
              <span className="badge rounded-pill badge-soft-success">
                Open
              </span>
            )}
            {row?.pending_quotation_details?.status_code === "win" && (
              <span className="badge rounded-pill badge-soft-info">Win</span>
            )}
            {row?.pending_quotation_details?.status_code === "closed" && (
              <span className="badge rounded-pill badge-soft-warning">
                Closed
              </span>
            )}
            {row?.pending_quotation_details?.status_code === "lose" && (
              <span className="badge rounded-pill badge-soft-danger">Lose</span>
            )}
          </div>
        ),
      },
    ],
    [currentPage, perPage]
  );

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title mb-0">
                  Quotation Report
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>

              {/* <div className="d-flex align-items-center gap-2"> */}
              {window.innerWidth >= 768 && (
                <div className="col-12 col-md-4">
                  <div className="card-control position-relative">
                    <div className="icon-form">
                      <Search
                        placeholder="Enter Text ..."
                        value={query}
                        onChange={setQuery}
                        searchText={searchTextData}
                        loading={loadingSearch}
                      />
                    </div>
                  </div>
                </div>
              )}

              {permissionMap?.quotation_report?.export_quotation_report && (
                <div className="d-flex justify-content-end gap-2">
                  <Export
                    options={[
                      {
                        label: downloading
                          ? "Downloading..."
                          : exporting
                          ? "Generating..."
                          : "Export",
                        iconClass: downloading
                          ? "ti ti-download"
                          : exporting
                          ? "ti ti-loader"
                          : "ti ti-file-type-xls",
                        colorClass:
                          exporting || downloading
                            ? "text-muted"
                            : "text-primary",
                        onClick:
                          exporting || downloading ? undefined : exportFile,
                      },
                    ]}
                  />
                </div>
              )}
              {/* </div> */}
            </div>
          </div>
        </div>
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center justify-content-between">
              {window.innerWidth <= 768 && (
                <div className="col-12 col-md-4">
                  <div className="card-control position-relative">
                    <div className="icon-form">
                      <Search
                        placeholder="Enter Text ..."
                        value={query}
                        onChange={setQuery}
                        searchText={searchTextData}
                        loading={loadingSearch}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="col-12 col-md-2 date-col">
                <div className="card-control position-relative">
                  <div className="icon-form">
                    <span className="form-icon" aria-hidden>
                      <i className="ti ti-calendar" />
                    </span>
                    <DateRangePicker
                      initialSettings={{
                        autoUpdateInput: false,
                        locale: { cancelLabel: "Clear" },
                        ...defaulSetDateFilter,
                      }}
                      onCallback={(start, end) => {
                        setStartDate(start.format("YYYY-MM-DD"));
                        setEndDate(end.format("YYYY-MM-DD"));
                      }}
                      onCancel={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                    >
                      <input
                        type="text"
                        className="form-control bookingrange"
                        placeholder="Select Date Range Filter"
                        value={
                          startDate && endDate
                            ? `${dayjs(startDate).format(
                                "DD-MM-YYYY"
                              )} - ${dayjs(endDate).format("DD-MM-YYYY")}`
                            : ""
                        }
                        readOnly
                        style={{ cursor: "pointer" }}
                        aria-label="Select date range"
                      />
                    </DateRangePicker>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Owner"
                  endpoint="getOwnerDD"
                  selected={ownerFilter}
                  onChange={(v) => setOwnerFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Principal"
                  endpoint="getPrincipalDD"
                  selected={principalFilter}
                  onChange={(v) => setPrincipalFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Branch"
                  endpoint="getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Currency"
                  endpoint="getCurrencyDD"
                  selected={currencyFilter}
                  onChange={(v) => setCurrencyFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-2">
                <FilterDropdown
                  label="Status"
                  endpoint="getStatusDD"
                  selected={statusFilter}
                  onChange={(v) => setStatusFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        {/* TABLE CARD: table + pagination */}
        <div className="card">
          <div className="card-body">
            {loading ? (
              <Loader />
            ) : (
              <div className="table-responsive">
                <Table data={list} columns={columns} />
              </div>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(page) => setCurrentPage(page)}
            onPerPageChange={(pp) => {
              setPerPage(pp);
              setCurrentPage(1);
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default QuotationReport;
